/// <reference types="vite/client" />

import { authApi, authQueryKeys } from '@entities/auth'
import { useGlobalPolling } from '@features/onboarding'
import { TOKEN_KEY } from '@shared/api/client'
import { ENV } from '@shared/constants/env'
import { SERVICE_NAME } from '@shared/constants/service'
import { MobileShell } from '@shared/ui/MobileShell'
import { PollingToast } from '@shared/ui/polling-toast'
import { Toaster } from '@shared/ui/sonner'
import type { QueryClient } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRootRouteWithContext, HeadContent, Outlet, Scripts } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import type { ReactNode } from 'react'
import appCss from '~/app/styles/globals.css?url'

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
}>()({
  beforeLoad: async ({ context }) => {
    if (typeof window !== 'undefined' && ENV.ENABLE_MOCKS) {
      const { initMocks } = await import('~/shared/mocks/init')
      await initMocks()
    }

    // 토큰이 있으면 유저 정보 prefetch
    if (typeof window !== 'undefined' && localStorage.getItem(TOKEN_KEY)) {
      context.queryClient.prefetchQuery({
        queryKey: authQueryKeys.me(),
        queryFn: () => authApi.getMe(),
        staleTime: Infinity,
      })
    }
  },
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { title: `${SERVICE_NAME} — 사주 명리학으로 읽는 나만의 인생 해석서` },
      {
        name: 'viewport',
        content:
          'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover',
      },
      {
        name: 'description',
        content:
          '정통 명리학 기반 사주팔자 분석. 오행 차트, 월별 운세, 맞춤 조언까지. 3분이면 충분해요.',
      },
      {
        property: 'og:title',
        content: `${SERVICE_NAME} — 사주 명리학으로 읽는 나만의 인생 해석서`,
      },
      {
        property: 'og:description',
        content:
          '정통 명리학 기반 사주팔자 분석. 오행 차트, 월별 운세, 맞춤 조언까지. 3분이면 충분해요.',
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:locale', content: 'ko_KR' },
      { property: 'og:site_name', content: SERVICE_NAME },
      { property: 'og:image', content: `${ENV.SITE_URL}/og-image.png` },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: `${ENV.SITE_URL}/og-image.png` },
      {
        name: 'twitter:title',
        content: `${SERVICE_NAME} — 사주 명리학으로 읽는 나만의 인생 해석서`,
      },
      {
        name: 'twitter:description',
        content:
          '정통 명리학 기반 사주팔자 분석. 오행 차트, 월별 운세, 맞춤 조언까지. 3분이면 충분해요.',
      },
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      { rel: 'icon', href: '/favicon.ico' },
    ],
  }),
  component: RootComponent,
})

function RootComponent() {
  useGlobalPolling()

  return (
    <RootDocument>
      <MobileShell>
        <Outlet />
      </MobileShell>
      <PollingToast />
    </RootDocument>
  )
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <html lang="ko" className="light">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Toaster />
        <TanStackRouterDevtools position="bottom-right" />
        <ReactQueryDevtools buttonPosition="bottom-left" />
        <Scripts />
      </body>
    </html>
  )
}
